import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Hexagon, ShieldCheck, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, complete2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA challenge state
  const [challenge, setChallenge] = useState(null); // { otp_token, email_hint }
  const [method, setMethod] = useState("totp"); // totp | recovery | email
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      if (result?.require_2fa) {
        setChallenge({ otp_token: result.otp_token, email_hint: result.email_hint });
        setMethod("totp");
        setCode("");
        return;
      }
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await complete2fa(challenge.otp_token, method, code, trustDevice);
      toast.success("Welcome back");
      // If user did NOT pre-check the box, offer a one-tap toast prompt to trust this device.
      if (!trustDevice) {
        toast("Trust this device?", {
          description: "Skip 2FA for 90 days when signing in from here.",
          duration: 12000,
          action: {
            label: "Trust",
            onClick: async () => {
              try {
                const { data } = await api.post("/auth/trusted-devices/grant");
                if (data?.trusted_device_token) {
                  localStorage.setItem("beekeep_trusted_device_token", data.trusted_device_token);
                  toast.success("This device is now trusted for 90 days");
                }
              } catch (err) {
                toast.error(err.response?.data?.detail || "Could not trust device");
              }
            },
          },
        });
      }
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const sendEmailOtp = async () => {
    setEmailSending(true);
    try {
      await api.post("/auth/2fa/send-email-otp", { otp_token: challenge.otp_token });
      setMethod("email");
      setCode("");
      toast.success(`Code sent to ${challenge.email_hint}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send email");
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
        </Link>

        {!challenge && (
          <div className="bg-white rounded-3xl border border-stone-200 p-8">
            <h1 className="font-display text-3xl font-medium">Welcome back</h1>
            <p className="text-stone-500 text-sm mt-1">Sign in to tend to your hives.</p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="login-email-input" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-amber-700 font-medium hover:underline"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input id="password" type="password" required maxLength={200} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="login-password-input" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(Boolean(v))}
                  className="h-4 w-4 rounded-[4px] border-stone-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400 data-[state=checked]:text-amber-900"
                  data-testid="login-remember-me-checkbox"
                />
                <Label htmlFor="remember-me" className="text-sm text-stone-600 font-normal cursor-pointer select-none">
                  Remember me on this device
                </Label>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="login-submit-button">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>

            <p className="text-sm text-stone-500 mt-6 text-center">
              New here?{" "}
              <Link to="/register" className="text-amber-700 font-medium hover:underline" data-testid="link-to-register">Create an account</Link>
            </p>
          </div>
        )}

        {challenge && (
          <div className="bg-white rounded-3xl border border-stone-200 p-8" data-testid="login-2fa-challenge">
            <div className="flex items-center gap-2 text-amber-700 mb-3">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">Two-factor verification</span>
            </div>
            <h1 className="font-display text-3xl font-medium">Almost in</h1>
            <p className="text-stone-500 text-sm mt-1">
              {method === "totp" && "Enter the 6-digit code from your authenticator app."}
              {method === "recovery" && "Enter one of your 8-character recovery codes."}
              {method === "email" && <>We sent a code to <strong>{challenge.email_hint}</strong>.</>}
            </p>

            <form onSubmit={submit2fa} className="mt-6 space-y-4">
              <div>
                <Label>Verification code</Label>
                <Input
                  type="text"
                  inputMode={method === "recovery" ? "text" : "numeric"}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={method === "recovery" ? "ABCD-1234" : "123 456"}
                  className="mt-1.5 font-mono tracking-[0.3em] text-center text-lg"
                  required
                  data-testid="login-2fa-code-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="login-2fa-submit">
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>

              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="trust-device"
                  checked={trustDevice}
                  onCheckedChange={(v) => setTrustDevice(Boolean(v))}
                  className="h-4 w-4 rounded-[4px] border-stone-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400 data-[state=checked]:text-amber-900"
                  data-testid="login-trust-device-checkbox"
                />
                <Label htmlFor="trust-device" className="text-sm text-stone-600 font-normal cursor-pointer select-none">
                  Trust this device for 90 days
                </Label>
              </div>
            </form>

            <div className="mt-6 pt-5 border-t border-stone-200 space-y-2 text-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-500">Other ways</div>
              {method !== "totp" && (
                <button type="button" onClick={() => { setMethod("totp"); setCode(""); }} className="flex items-center gap-2 text-amber-700 hover:underline" data-testid="login-2fa-use-totp">
                  <KeyRound className="w-4 h-4" /> Use authenticator app
                </button>
              )}
              {method !== "email" && (
                <button type="button" onClick={sendEmailOtp} disabled={emailSending} className="flex items-center gap-2 text-amber-700 hover:underline disabled:opacity-50" data-testid="login-2fa-send-email">
                  <Mail className="w-4 h-4" /> {emailSending ? "Sending…" : `Email me a code (${challenge.email_hint})`}
                </button>
              )}
              {method !== "recovery" && (
                <button type="button" onClick={() => { setMethod("recovery"); setCode(""); }} className="flex items-center gap-2 text-amber-700 hover:underline" data-testid="login-2fa-use-recovery">
                  <ShieldCheck className="w-4 h-4" /> Use a recovery code
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => { setChallenge(null); setCode(""); setPassword(""); }}
              className="mt-6 text-xs text-stone-500 hover:text-stone-800"
              data-testid="login-2fa-cancel"
            >
              ← Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
